import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { ApiClient } from '../../api/client';
import { DEFAULT_FORM_MEDIA, FormMediaContext, demoMediaUrl, enumImagesOf, useFormMedia, type FormMedia } from './FormMedia';
import type { MediaRef } from './presentation';

/**
 * Résolution des images d'un formulaire (ADR-106, addendum du 2026-10-21 ; ADR-146 addendum (20)) : chaque
 * référence de médiathèque du gabarit et des images servies avec la tâche (`file_id` d'une image du tenant ou
 * de la bibliothèque commune, `common_key` d'une image commune) devient une adresse signée par
 * `GetFileDownloads`, sous l'autorisation de l'utilisateur. Une référence introuvable reste sans image
 * (repli : icône, puis libellé), jamais une erreur. Aucune URL lue dans le gabarit n'est chargée (R3).
 */

const FILE_ID = /^[0-9a-f]{24}$/;
const COMMON_KEY = /^[a-z][a-z0-9_]*(\.[a-z0-9_]+){1,4}$/;
const BATCH = 100;
const MARGIN_MS = 30_000;
const MISSING_MS = 5 * 60_000;

/** Clé de cache d'une référence : `f:<identifiant>` ou `k:<clé commune>`. */
export function refKey(ref: MediaRef | undefined): string | undefined {
  if (ref === undefined || ref === null || typeof ref !== 'object') return undefined;
  if (typeof ref.file_id === 'string' && FILE_ID.test(ref.file_id)) return `f:${ref.file_id}`;
  if (typeof ref.common_key === 'string' && COMMON_KEY.test(ref.common_key)) return `k:${ref.common_key}`;
  return undefined;
}

/**
 * Références de médiathèque d'un ou plusieurs documents (gabarit, images d'énumération, props d'un bloc) :
 * tout objet qui porte `common_key` (clé commune) ou un `file_id` hexadécimal sous une clé `image`.
 */
export function collectMediaRefs(...values: unknown[]): MediaRef[] {
  const out = new Map<string, MediaRef>();
  const visit = (value: unknown, parentKey: string | undefined, depth: number): void => {
    if (depth > 40 || value === null || typeof value !== 'object') return;
    if (Array.isArray(value)) {
      for (const entry of value) visit(entry, parentKey, depth + 1);
      return;
    }
    const record = value as Record<string, unknown>;
    const commonKey = record['common_key'];
    const fileId = record['file_id'];
    if (typeof commonKey === 'string' && COMMON_KEY.test(commonKey)) out.set(`k:${commonKey}`, { common_key: commonKey });
    else if (typeof fileId === 'string' && FILE_ID.test(fileId) && (parentKey === 'image' || parentKey === '$literal' || parentKey === 'enum_image')) out.set(`f:${fileId}`, { file_id: fileId });
    for (const [key, entry] of Object.entries(record)) visit(entry, key, depth + 1);
  };
  for (const value of values) visit(value, undefined, 0);
  return [...out.values()];
}

interface Cached {
  url: string | null;
  expires_at: number;
}
const cache = new Map<string, Cached>();

/** Pour les tests : repartir d'un cache vide. */
export function clearMediaLinks(): void {
  cache.clear();
}

const fresh = (entry: Cached | undefined, now: number): entry is Cached => entry !== undefined && entry.expires_at - MARGIN_MS > now;

function linksOf(keys: readonly string[], now: number): Map<string, string> {
  const out = new Map<string, string>();
  for (const key of keys) {
    const entry = cache.get(key);
    if (fresh(entry, now) && entry.url !== null) out.set(key, entry.url);
  }
  return out;
}

type Downloads = { items: Array<{ file_id: string; common_key?: string; download: { url: string; expires_at: string } }>; missing: string[] };

/** Adresses signées des références, par clé de cache ; lues par lots de 100, gardées jusqu'à leur expiration. */
export function useMediaRefLinks(client: ApiClient | undefined, refs: readonly MediaRef[]): ReadonlyMap<string, string> {
  const keys = [...new Set(refs.map(refKey).filter((key): key is string => key !== undefined))].sort();
  const signature = keys.join(',');
  const [links, setLinks] = useState<ReadonlyMap<string, string>>(() => linksOf(keys, Date.now()));
  useEffect(() => {
    let alive = true;
    const now = Date.now();
    setLinks(linksOf(keys, now));
    if (client === undefined) return;
    const wanted = keys.filter((key) => !fresh(cache.get(key), now));
    if (wanted.length === 0) return;
    const batches: string[][] = [];
    for (let index = 0; index < wanted.length; index += BATCH) batches.push(wanted.slice(index, index + BATCH));
    void Promise.allSettled(
      batches.map((batch) => {
        const ids = batch.filter((key) => key.startsWith('f:')).map((key) => key.slice(2));
        const commonKeys = batch.filter((key) => key.startsWith('k:')).map((key) => key.slice(2));
        return client.query<Downloads>('GetFileDownloads', { ...(ids.length === 0 ? {} : { file_ids: ids.join(',') }), ...(commonKeys.length === 0 ? {} : { common_keys: commonKeys.join(',') }), media_type_prefix: 'image/' });
      }),
    ).then((results) => {
      const at = Date.now();
      results.forEach((result, index) => {
        if (result.status !== 'fulfilled') return;
        const served = new Set<string>();
        for (const item of result.value.data.items) {
          const expires = Date.parse(item.download.expires_at);
          const entry = { url: item.download.url, expires_at: Number.isNaN(expires) ? at : expires };
          // Le serveur rend les identifiants demandés puis les clés communes : un identifiant demandé se range
          // sous le sien, une clé commune sous sa clé.
          const byId = `f:${item.file_id}`;
          const key = batches[index]!.includes(byId) && !served.has(byId) ? byId : `k:${item.common_key ?? ''}`;
          cache.set(key, entry);
          served.add(key);
        }
        for (const key of batches[index]!) if (!served.has(key)) cache.set(key, { url: null, expires_at: at + MISSING_MS + MARGIN_MS });
      });
      if (alive) setLinks(linksOf(keys, at));
    });
    return () => {
      alive = false;
    };
    // La signature résume les références : une autre liste relance la lecture de ce qui manque.
  }, [client, signature]);
  return links;
}

/** Client de l'API pour les images des formulaires montrés hors d'une tâche (aperçu du studio). */
export const MediaClientContext = createContext<ApiClient | undefined>(undefined);

/**
 * Fournit au formulaire la résolution de ses images : celles du gabarit et celles servies avec la tâche
 * (`option_images`). Sans client (essai, test), la résolution reste celle de démonstration.
 */
export function MediaRefProvider({ client, template, optionImages, children }: { client?: ApiClient | undefined; template: unknown; optionImages?: Readonly<Record<string, Readonly<Record<string, MediaRef>>>> | undefined; children: ReactNode }) {
  const fromContext = useContext(MediaClientContext);
  const api = client ?? fromContext;
  const outer = useFormMedia();
  const refs = useMemo(() => collectMediaRefs(template, optionImages), [template, optionImages]);
  const links = useMediaRefLinks(api, refs);
  const media = useMemo<FormMedia>(() => {
    const enumImages = optionImages === undefined ? outer.enumImages : { ...(outer.enumImages ?? {}), ...enumImagesOf(template as Parameters<typeof enumImagesOf>[0], optionImages) };
    return {
      ...outer,
      ...(enumImages === undefined ? {} : { enumImages }),
      resolve: (ref) => {
        const key = refKey(ref);
        const url = key === undefined ? undefined : links.get(key);
        return url ?? (outer.resolve === DEFAULT_FORM_MEDIA.resolve ? demoMediaUrl(ref) : outer.resolve(ref));
      },
    };
  }, [outer, links, template, optionImages]);
  return <FormMediaContext.Provider value={media}>{children}</FormMediaContext.Provider>;
}
